import { CompareProducts } from '@iheartjane/menu';
import { useMobileMediaQuery } from '@iheartjane/reefer';
import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { Outlet } from 'react-router';

import { SearchProvider } from '@jane/search/hooks';
import {
  EmptyMenuProductGrid,
  PageLayout,
} from '@jane/shared-ecomm/components';
import {
  MenuAdsProvider,
  SpecialsProvider,
  useBrandSpecialsContext,
  useMenu,
  useSpecials,
} from '@jane/shared-ecomm/providers';
import { getMenuTabs } from '@jane/shared-ecomm/util';
import { useShouldShowGold } from '@jane/shared/hooks';

import { CompareHandlersRegistrar } from './MenuComponents/CompareHandlersRegistrar/CompareHandlersRegistrar';
import { MenuBanners } from './MenuComponents/MenuBanners/MenuBanners';
import { MenuCompareProvider } from './MenuComponents/MenuCompareProvider/MenuCompareProvider';
import { MenuHeader } from './MenuComponents/MenuHeader/MenuHeader';
import { MenuResetLocationLink } from './MenuComponents/MenuResetLocationLink/MenuResetLocationLink';
import { MenuSeo } from './MenuComponents/MenuSeo/MenuSeo';
import { MenuSpecialBundleEnticement } from './MenuComponents/MenuSpecialBundleEnticement/MenuSpecialBundleEnticement';

const MenuAdsGate = ({ children }: PropsWithChildren) => {
  const {
    appInfo: { appMode },
  } = useMenu();

  return appMode === 'framelessEmbed' ? (
    <MenuAdsProvider>{children}</MenuAdsProvider>
  ) : (
    <>{children}</>
  );
};

const MenuInner = () => {
  const isMobile = useMobileMediaQuery({});

  const {
    appInfo: { appMode },
    dispatches: { attemptToAddToCart },
    facets,
    store,
  } = useMenu();
  const shouldShowGold = useShouldShowGold({
    store: { id: store.id, state: store.state },
  });

  const { specialIds, specials } = useSpecials();
  const { brandSpecials } = useBrandSpecialsContext();

  const hasVisibleSpecials = specials?.some(
    (special) => special.hidden_from_page !== true
  );

  const specialsPage =
    (store.specials_row_configs?.enabled ||
      store.custom_rows?.includes('sale')) &&
    Boolean(specialIds?.length) &&
    hasVisibleSpecials;

  const menuTabs = useMemo(
    () =>
      getMenuTabs({
        facets,
        isMarketplace: appMode === 'default',
        menuTabs: store.menu_tabs,
        specialsPage,
        showGoldTab: shouldShowGold && brandSpecials?.length > 0,
      }),
    [
      appMode,
      brandSpecials?.length,
      facets,
      shouldShowGold,
      specialsPage,
      store.menu_tabs,
    ]
  );

  return (
    <>
      <MenuSeo />
      <MenuSpecialBundleEnticement />
      <MenuHeader tabs={menuTabs} />
      <PageLayout
        as="main"
        id="content"
        py={isMobile ? 24 : 48}
        overflowX="visible"
      >
        <MenuResetLocationLink />
        <MenuAdsGate>
          <MenuBanners tabs={menuTabs} />
          <EmptyMenuProductGrid />
          <Outlet />
        </MenuAdsGate>
      </PageLayout>
      <CompareHandlersRegistrar
        attemptToAddToCart={attemptToAddToCart}
        store={store}
      />
      <CompareProducts.Bar />
      <CompareProducts.Modal isMobile={isMobile} />
    </>
  );
};

export const Menu = () => {
  const { searchStateWithInitialQuery, onSearchStateChange, store } = useMenu();

  return (
    <SpecialsProvider fetchSpecialsRow>
      <SearchProvider
        indexPrefix="menu-products-"
        initialSearchState={searchStateWithInitialQuery}
        onSearchStateChange={onSearchStateChange}
      >
        <MenuCompareProvider store={store}>
          <MenuInner />
        </MenuCompareProvider>
      </SearchProvider>
    </SpecialsProvider>
  );
};
